import { Anticoagulant, ReasonDiscardProduct } from 'gql/types';
import {
  anticoagulantSchema,
  discardReasonSchema,
  processingProductTypeSchema,
} from 'modules/products/schemas';
import { isRespinable } from 'modules/products/utils/isRespinable';
import { getTranslation } from 'utils/getTranslation';
import * as yup from 'yup';

export type CreateProductSchema = {
  woocommerceCategoryId: number;
  quantity?: number;
  woocommerceProductId: number;
  anticoagulant?: Anticoagulant;
  storageRequirements: string[];
  values: number[];
  discarded?: boolean;
  processingType?: string;
  processorId?: string;
  discardedReason?: string;
  discardReasonDetails?: string;
  respin?: boolean;
  isMantelAssigned?: boolean;
};

const t = getTranslation('products.ValidationErrors');

export const createProductSchema: yup.SchemaOf<CreateProductSchema> =
  yup.object({
    woocommerceCategoryId: yup.number().required(t('isRequired')),
    quantity: yup.number().when('discarded', {
      is: false,
      then: () => yup.number().required(t('isRequired')),
    }),
    woocommerceProductId: yup.number().required(t('isRequired')),
    anticoagulant: anticoagulantSchema.when('discarded', {
      is: false,
      then: () => anticoagulantSchema.required(t('isRequired')),
    }),
    storageRequirements: yup.array().of(yup.string().required(t('isRequired'))),
    values: yup
      .array()
      .of(yup.number().required(t('isRequired')))
      .required(t('isRequired'))
      .when(['quantity'], (quantity) => {
        return yup
          .array()
          .of(yup.number().required(t('isRequired')))
          .min(quantity, t('minValue', { value: quantity }))
          .max(quantity, t('minValue', { value: quantity }));
      })
      .default([]),
    discarded: yup.boolean(),
    processingType: processingProductTypeSchema.when('discarded', {
      is: true,
      then: () => processingProductTypeSchema.required(t('isRequired')),
    }),
    processorId: yup.string().when('discarded', {
      is: true,
      then: () => yup.string().required(t('isRequired')),
    }),
    discardedReason: discardReasonSchema.when('discarded', {
      is: true,
      then: () => discardReasonSchema.required(t('isRequired')),
    }),
    discardReasonDetails: yup.string().when('discardedReason', {
      is: ReasonDiscardProduct.Other,
      then: () =>
        yup
          .string()
          .required(t('isRequired'))
          .min(5, t('minLength', { value: 5 }))
          .max(500, t('maxLength', { value: 500 })),
    }),
    respin: yup.mixed().when('woocommerceCategoryId', {
      is: (categoryId: number) => categoryId && isRespinable(categoryId),
      then: () => yup.boolean().defined().default(false),
      otherwise: () => yup.mixed().test((value) => value === undefined),
    }),
    isMantelAssigned: yup.boolean().optional(),
  });
